import { memo } from 'react';
import PropTypes from 'prop-types';

import { Box, Button, Badge } from '@mui/material';
import MIcon from '../MIcon';
import MActionButton from './MActionButton';

/**
 * A functional component that renders a set of action buttons.
 * Displays either a single button for one action or multiple action buttons in a grid-like layout.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {Array} props.actions - Array of action objects to render as buttons.
 *   Each action should contain:
 *   - `id` (string): Unique identifier for the button.
 *   - `icon` (string): Icon name for the button.
 *   - `text` (string): Text label for the button.
 *   - `handler` (function): Function to execute on button click.
 * @param {Array} [props.actionBarItems] - Additional items for the action bar. Reserved for swipeable drawers or specific use cases.
 *   Each item should contain:
 *   - `id` (string): Unique identifier for the button or item.
 *   - `icon` (string): Icon name for the button.
 *   - `text` (string): Text label for the button.
 *   - `handler` (function): Function to execute on button click.
 *   - `badgeContent` (number): Badge number to display on the button.
 *   - `caption` (string): Optional caption text for the button.
 *   - `iconVariant` (string): Icon variant (e.g., primary, secondary).
 *   - `textVariant` (string): Text variant for button styles (e.g., disabled).
 *   - `disabled` (boolean): Whether the action is in a disabled state.
 * @param {number} props.itemWidth - The width of each action button.
 *
 * @returns {JSX.Element} A set of action buttons or a single action button if only one action is provided.
 *
 */
const MActionButtons = ({ actions, actionBarItems, itemWidth }) => {
  // If there is only one action, we display a standard contained button
  if (actions.length === 1) {
    const { icon, id, handler, text, notificationCount } = actions[0];

    return (
      <Button id={id} variant="contained" startIcon={<MIcon icon={icon} />} onClick={handler}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {text}
          <Badge badgeContent={notificationCount || 0} color="error" />
        </Box>
      </Button>
    );
  }

  return actionBarItems.map((item, idx) => {
    // Dividers are reserved for swipeable drawers
    if (item.divider) {
      return;
    }

    return (
      <MActionButton
        id={item.id}
        key={`${item.text}-${item.id}-${idx}`}
        badgeContent={item.notificationCount || item.badgeContent}
        caption={item.caption}
        icon={item.icon}
        iconVariant={item.iconVariant}
        itemWidth={itemWidth}
        text={item.text}
        textVariant={item.textVariant}
        disabled={!!item.disabled}
        handleClick={item.handler}
      />
    );
  });
};

MActionButtons.propTypes = {
  actions: PropTypes.array.isRequired,
  actionBarItems: PropTypes.array,
  itemWidth: PropTypes.number.isRequired
};

export default memo(MActionButtons);
